import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { GESTURE_THRESHOLDS } from '../../../utils/gestureThresholds'
import { useHaptic } from '../../../hooks/useHaptic'

export interface PullToRefreshProps {
  /** Content to render */
  children: React.ReactNode

  /** Callback when refresh is triggered */
  onRefresh: () => Promise<void> | void

  /** Disable pull-to-refresh */
  disabled?: boolean

  /** Threshold in pixels to trigger refresh (default: 80) */
  threshold?: number

  /** Maximum pull distance in pixels (default: 120) */
  maxPull?: number
}

/**
 * PullToRefresh Component
 *
 * Enables pull-to-refresh gesture on scrollable content.
 *
 * Features:
 * - Touch gesture detection (pull down from top)
 * - Visual loading indicator with smooth animation
 * - Haptic feedback on trigger
 * - iOS/Android compatibility (prevents rubber band scroll)
 * - Configurable thresholds
 * - Accessibility support
 *
 * @example
 * ```tsx
 * <PullToRefresh onRefresh={async () => {
 *   await refetch()
 * }}>
 *   <YourContent />
 * </PullToRefresh>
 * ```
 */
export function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
  threshold = GESTURE_THRESHOLDS.pullToRefresh.trigger,
  maxPull = GESTURE_THRESHOLDS.pullToRefresh.maxPull,
}: PullToRefreshProps): JSX.Element {
  const [isPulling, setIsPulling] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshStatus, setRefreshStatus] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef<number>(0)
  const currentYRef = useRef<number>(0)

  const { light, medium } = useHaptic()

  /**
   * Handle touch start - only allow pull if at top of scroll
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing) return

      const scrollTop = containerRef.current?.scrollTop ?? 0

      // Only allow pull if at the top
      if (scrollTop === 0) {
        startYRef.current = e.touches[0].clientY
        currentYRef.current = e.touches[0].clientY
        setIsPulling(true)
        light() // Light haptic feedback on pull start
      }
    },
    [disabled, isRefreshing, light]
  )

  /**
   * Handle touch move - track pull distance
   */
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling || disabled || isRefreshing) return

      currentYRef.current = e.touches[0].clientY
      const distance = currentYRef.current - startYRef.current

      // Only track downward pulls
      if (distance > 0) {
        // Cap at maxPull
        const cappedDistance = Math.min(distance, maxPull)
        setPullDistance(cappedDistance)

        // Prevent default scroll behavior on iOS
        if (e.cancelable) {
          e.preventDefault()
        }
      }
    },
    [isPulling, disabled, isRefreshing, maxPull]
  )

  /**
   * Handle touch end - trigger refresh if threshold met
   */
  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return

    setIsPulling(false)

    // Check if pull distance exceeds threshold
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      setRefreshStatus('Refreshing...')
      medium() // Medium haptic feedback on refresh trigger

      try {
        await onRefresh()
        setRefreshStatus('Refreshed')
      } catch (error) {
        console.error('Refresh failed:', error)
        setRefreshStatus('Refresh failed')
      } finally {
        // Small delay before resetting to show completion
        setTimeout(() => {
          setIsRefreshing(false)
          setPullDistance(0)
          setRefreshStatus('')
        }, 300)
      }
    } else {
      // Reset if threshold not met
      setPullDistance(0)
    }
  }, [isPulling, disabled, pullDistance, threshold, isRefreshing, onRefresh, medium])

  /**
   * Reset on unmount
   */
  useEffect(() => {
    return () => {
      setIsPulling(false)
      setIsRefreshing(false)
      setPullDistance(0)
    }
  }, [])

  // Calculate indicator opacity and scale based on pull distance
  const indicatorOpacity = Math.min(pullDistance / threshold, 1)
  const indicatorScale = Math.min(pullDistance / threshold, 1)

  return (
    <Box
      ref={containerRef}
      data-testid="pull-to-refresh-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Pull indicator */}
      <Box
        data-testid="pull-indicator"
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: `translateX(-50%) translateY(${Math.min(pullDistance - 40, 0)}px) scale(${indicatorScale})`,
          opacity: isPulling || isRefreshing ? indicatorOpacity : 0,
          transition: isPulling ? 'none' : 'opacity 0.3s ease, transform 0.3s ease',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        {isRefreshing ? (
          <CircularProgress size={24} role="progressbar" />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'primary.main',
              borderTopColor: 'transparent',
              transform: `rotate(${pullDistance * 2}deg)`,
            }}
          />
        )}
      </Box>

      {/* Accessibility: Live region for screen readers */}
      {refreshStatus && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute',
            left: -10000,
            width: 1,
            height: 1,
            overflow: 'hidden',
          }}
        >
          {refreshStatus}
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          transform: isPulling ? `translateY(${Math.min(pullDistance * 0.5, maxPull * 0.5)}px)` : 'none',
          transition: isPulling ? 'none' : 'transform 0.3s ease',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
